import { Button, Label, TargetIcon, Typography } from 'procyon-ui'
import React, { useState } from 'react'
import { useChannelBrowser } from '../../../providers/hooks'
import useMultiSlice from 'Core/Hooks/useMultiSlice'
import { SelectChannel } from '../../Modal'
import { enqueueNotification } from 'Utils/Helpers'
import _ from 'lodash'
import { createRsrcKey } from 'features/resources'
import { getFriendlyName } from 'Utils/FriendlyName'
import { reduxApiClient } from 'infra'
import { LoadingFeedback } from 'V2Components'

const NOTIFICATION_TYPES = {
  GATEWAY: 'getway',
  PRIVATE: 'private'
}

const SlackNotificationChannel = () => {
  const { linkedChannels, refresh, integration } = useChannelBrowser()
  const { slices, getObjectRef } = useMultiSlice(['notificationSubscriptions', 'slackChannelList'])
  const [showModal, setShowModal] = useState(false)
  const [loading, setLoading] = useState(false)
  const [notificationType, setNotificationType] = useState('')
  const [activeChannel, setActiveChannel] = useState(null)
  const [isWebhookChannelSelected, setIsWebhookChannelSelected] = useState(false)

  // Utility Functions
  const hasWebhookUrl = () => integration?.Spec?.WebhookUrl !== ''

  // Gateway Channel Helpers
  const getGatewayChannel = () => {
    const channelRefId =
      integration?.SlackIntegrationSettings?.SlackIntegrationSettingsMap
        ?.medusaNodeSlackNotificationChannelRef?.RefID

    if (!channelRefId) return false
    if (hasWebhookUrl() && channelRefId) return true

    const channel = linkedChannels.find((ch) => ch.ObjectMeta.ID === channelRefId)
    return channel || false
  }

  // Private Channel Helpers
  const getPrivateNotificationSubscription = () => {
    return slices.notificationSubscriptions.find((subscription) => {
      const { SubscriptionFor, Subscriber } = subscription.Spec
      return (
        SubscriptionFor.RefID === '0' &&
        SubscriptionFor.RefKind === 'Server' &&
        Subscriber.RefKind === 'SlackChannel'
      )
    })
  }

  const getWebhookPrivateChannel = () => {
    return slices.notificationSubscriptions.find((subscription) => {
      const { Subscriber } = subscription.Spec
      return (
        hasWebhookUrl() &&
        Subscriber.RefKind === 'SlackIntegration' &&
        Subscriber.RefID === integration?.ObjectMeta?.ID
      )
    })
  }

  const getPrivateServerChannel = () => {
    const privateSubscription = getPrivateNotificationSubscription()
    if (privateSubscription) {
      const { Subscriber } = privateSubscription.Spec
      const channel = getObjectRef(Subscriber)
      console.log('[channel](subs):', Subscriber, channel)
      return channel
    }
    return null
  }

  // UI Helper Functions - Gateway
  const gatewayChannel = getGatewayChannel()

  const renderGatewayIcon = () => {
    const hasChannel = gatewayChannel && Object.keys(gatewayChannel).length > 1
    return hasChannel || hasWebhookUrl() ? <TargetIcon type='SLACK' /> : ''
  }

  const getGatewayVariant = () => {
    return gatewayChannel && Object.keys(gatewayChannel).length > 1 ? 'grayBlue' : 'gray'
  }

  const renderGatewayChannelName = () => {
    if (gatewayChannel && !hasWebhookUrl()) {
      return <strong>{gatewayChannel?.Spec?.ChannelName}</strong>
    }
    if (gatewayChannel && hasWebhookUrl()) {
      return <strong>Webhook Channel</strong>
    }
    return <em>No channel connected yet</em>
  }

  // UI Helper Functions - Private
  const privateServerChannel = getPrivateServerChannel()

  const renderPrivateIcon = () => {
    const hasChannel = privateServerChannel && Object.keys(privateServerChannel).length > 1
    return hasChannel || hasWebhookUrl() ? <TargetIcon type='SLACK' /> : ''
  }

  const getPrivateVariant = () => {
    return privateServerChannel && Object.keys(privateServerChannel).length > 1
      ? 'grayBlue'
      : 'gray'
  }

  const renderPrivateChannelName = () => {
    if (getWebhookPrivateChannel() && hasWebhookUrl()) {
      return <strong>Webhook Channel</strong>
    }
    if (privateServerChannel) {
      return <strong>{privateServerChannel.Spec.ChannelName}</strong>
    }
    return <em>No channel connected yet</em>
  }

  // Modal Handlers
  const handleModalClick = (type) => {
    setNotificationType(type)

    // Set initial webhook selection state based on current configuration
    if (type === NOTIFICATION_TYPES.GATEWAY) {
      setIsWebhookChannelSelected(hasWebhookUrl() && gatewayChannel)
      setActiveChannel(gatewayChannel || null)
    } else if (type === NOTIFICATION_TYPES.PRIVATE) {
      setIsWebhookChannelSelected(hasWebhookUrl() && getWebhookPrivateChannel())
      setActiveChannel(privateServerChannel || null)
    }

    setShowModal(true)
  }

  const handleChannelSelection = (newValue) => {
    setIsWebhookChannelSelected(newValue)
  }

  const handleChannelSelect = (channel) => {
    setActiveChannel(channel)
  }

  // API Operations - Gateway
  const updateGatewayChannel = async (selectedChannel) => {
    try {
      setLoading(true)
      const payload = _.cloneDeep(integration)

      // Determine the channel reference based on webhook or selected channel
      if (hasWebhookUrl()) {
        // Use webhook integration reference
        payload.SlackIntegrationSettings.SlackIntegrationSettingsMap.medusaNodeSlackNotificationChannelRef =
          {
            RefID: integration?.ObjectMeta?.ID,
            RefKind: 'SlackIntegration'
          }
      } else if (selectedChannel && Object.keys(selectedChannel).length > 1) {
        // Use selected Slack channel reference
        payload.SlackIntegrationSettings.SlackIntegrationSettingsMap.medusaNodeSlackNotificationChannelRef =
          {
            RefID: selectedChannel.ObjectMeta.ID,
            RefKind: 'SlackChannel'
          }
      } else {
        // Clear the channel reference
        payload.SlackIntegrationSettings.SlackIntegrationSettingsMap.medusaNodeSlackNotificationChannelRef =
          {}
      }

      await reduxApiClient('slackintegrations').update(payload)
      enqueueNotification('Gateway Notification channel updated successfully.', 'info')
    } catch (error) {
      enqueueNotification('Operation Failed', 'error')
    } finally {
      setLoading(false)
      setShowModal(false)
    }
  }

  // API Operations - Private
  const updatePrivateChannel = async (selectedChannel) => {
    try {
      setLoading(true)

      // Handle deselection - no channel and no webhook
      if (!Object.keys(selectedChannel || {}).length && !hasWebhookUrl()) {
        const privateSubscription = getPrivateNotificationSubscription()
        if (privateSubscription) {
          await reduxApiClient('notificationsubscriptions').delete(privateSubscription)
          refresh()
          setShowModal(false)
        }
        return
      }

      // Determine the subscriber reference
      let selectedChannelRef = {}

      if (hasWebhookUrl()) {
        selectedChannelRef = {
          RefKind: 'SlackIntegration',
          RefID: integration?.ObjectMeta?.ID
        }
      } else {
        selectedChannelRef = {
          RefKind: 'SlackChannel',
          RefID: selectedChannel.ObjectMeta.ID
        }
      }

      const channelKey = createRsrcKey(selectedChannelRef)
      const existingSubscription = getPrivateNotificationSubscription()

      // Avoid duplicate operation
      if (privateServerChannel && channelKey === createRsrcKey(privateServerChannel)) {
        setLoading(false)
        return
      }

      // Delete existing subscription if present
      if (existingSubscription) {
        await reduxApiClient('notificationsubscriptions').delete(existingSubscription)
      }

      // Create new subscription
      const newSubscription = {
        ObjectMeta: {
          Name: `PRIVATE_${getFriendlyName()}`
        },
        Spec: {
          SubscriptionFor: {
            RefID: '0',
            RefKind: 'Server'
          },
          Subscriber: selectedChannelRef
        }
      }

      await reduxApiClient('notificationsubscriptions').create(newSubscription)
      enqueueNotification('Private Server Notification Channel changed successfully!', 'info')
      refresh()
    } catch (error) {
      enqueueNotification('Failed to change Notification Channel!', 'error')
      refresh()
    } finally {
      setShowModal(false)
      setLoading(false)
    }
  }

  const onConfirmChannelChange = (channelData, type) => {
    // Check if webhook is selected
    if (channelData?.isWebhook || isWebhookChannelSelected) {
      if (type === NOTIFICATION_TYPES.GATEWAY) {
        updateGatewayChannel(null)
      } else {
        updatePrivateChannel({})
      }
    } else {
      if (type === NOTIFICATION_TYPES.GATEWAY) {
        updateGatewayChannel(channelData || activeChannel)
      } else {
        updatePrivateChannel(channelData || activeChannel)
      }
    }
  }

  return (
    <div className='mt-8'>
      {/* Gateway Notification Channel */}
      <div className='border-b-2'>
        <Typography variant='h4-medium'>GateWay Notification Channel</Typography>
        <div className='flex justify-between items-center my-5'>
          <Label
            size='lg'
            icon={renderGatewayIcon()}
            text={renderGatewayChannelName()}
            variant={getGatewayVariant()}
            style={{ padding: '15px' }}
          />
          <Button
            id='select-getway-button'
            size='md'
            variant='gray'
            onClick={() => handleModalClick(NOTIFICATION_TYPES.GATEWAY)}
          >
            Select
          </Button>
        </div>
      </div>

      {/* Private Server Notification Channel */}
      <div className='border-b-2 mt-10'>
        <Typography variant='h4-medium'>Private Server Notification Channel</Typography>
        <div className='flex justify-between items-center my-5'>
          <Label
            size='lg'
            icon={renderPrivateIcon()}
            text={renderPrivateChannelName()}
            variant={getPrivateVariant()}
            style={{ padding: '15px' }}
          />
          <Button
            id='select-private-button'
            size='md'
            variant='gray'
            onClick={() => handleModalClick(NOTIFICATION_TYPES.PRIVATE)}
          >
            Select
          </Button>
        </div>
      </div>

      <SelectChannel
        showModal={showModal}
        activeChannel={activeChannel}
        setShowModal={setShowModal}
        onConfirmChannelChange={onConfirmChannelChange}
        notificationType={notificationType}
        handleChannelSelect={handleChannelSelect}
        handleChannelSelection={handleChannelSelection}
        isWebhookChannelSelected={isWebhookChannelSelected}
      />

      <LoadingFeedback
        loading={loading}
        message='Updating Slack Notification Channel'
        caption='Please wait..'
      />
    </div>
  )
}

export { SlackNotificationChannel }
